import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, Clock, ChefHat, PartyPopper, XCircle, Store } from "lucide-react";

const STEPS = [
  { key: "new", label: "Order received", icon: Clock },
  { key: "preparing", label: "Being prepared", icon: ChefHat },
  { key: "ready", label: "Ready for pickup", icon: PartyPopper },
  { key: "picked_up", label: "Collected", icon: CheckCircle2 },
];

export default function OrderStatus({ slugOverride }) {
  const { slug: paramSlug, orderId } = useParams();
  const slug = slugOverride || paramSlug;
  const home = slugOverride ? "/" : `/b/${slug}`;
  const [o, setO] = useState(null);
  const [missing, setMissing] = useState(false);
  const last = useRef(null);

  useEffect(() => {
    let timer;
    const load = async () => {
      try {
        const { data } = await api.get(`/public/orders/${orderId}`);
        if (last.current && last.current !== "ready" && data.status === "ready") {
          try { navigator.vibrate?.([300, 150, 300]); } catch { /* not supported */ }
        }
        last.current = data.status;
        setO(data);
        document.title = data.status === "ready" ? `READY - token #${data.token_no}` : `Token #${data.token_no} - ${data.shop_name}`;
        if (!["picked_up", "cancelled"].includes(data.status)) timer = setTimeout(load, 6000);
      } catch { setMissing(true); }
    };
    load();
    return () => clearTimeout(timer);
  }, [orderId]);

  if (missing) return <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-5 text-center"><Store className="w-12 h-12 text-primary" /><h1 className="font-display text-2xl font-bold">Order not found</h1><Link to={home}><Button className="rounded-full bg-primary">Back to shop</Button></Link></div>;
  if (!o) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const cancelled = o.status === "cancelled";
  const idx = STEPS.findIndex((s) => s.key === o.status);
  const ready = o.status === "ready";

  return (
    <div className="min-h-screen px-5 py-8">
      <div className="max-w-md mx-auto">
        <p className="text-center text-sm text-muted-foreground">{o.shop_name}</p>
        <div className={`mt-4 rounded-2xl border p-8 text-center ${ready ? "border-green-500/60 bg-green-500/10" : cancelled ? "border-red-500/40 bg-red-500/5" : "border-white/10 bg-card"}`} data-testid="order-token-card">
          <p className="overline text-muted-foreground">Your token</p>
          <p className="font-display text-7xl font-extrabold tracking-tight mt-1" data-testid="order-token">#{o.token_no}</p>
          <p className="mt-2 text-sm">{o.customer_name}</p>
          <p className={`mt-4 text-lg font-semibold ${ready ? "text-green-400" : cancelled ? "text-red-400" : "text-primary"}`} data-testid="order-status-text">
            {cancelled ? "Cancelled by the shop" : ready ? "Your food is ready — show this token at the counter 🎉" : STEPS[idx]?.label}
          </p>
        </div>

        {!cancelled && (
          <ol className="mt-6 space-y-3" data-testid="order-steps">
            {STEPS.map((s, i) => (
              <li key={s.key} className={`flex items-center gap-3 text-sm ${i <= idx ? "text-white" : "text-muted-foreground"}`}>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center border ${i <= idx ? "bg-primary border-primary" : "border-white/15"}`}><s.icon className="w-4 h-4" /></span>
                {s.label}
              </li>
            ))}
          </ol>
        )}

        <div className="mt-6 bg-card border border-white/10 rounded-xl p-4 text-sm">
          {o.items.map((i) => <div key={i.offer_id} className="flex justify-between py-1"><span>{i.qty} × {i.title}</span><span className="text-muted-foreground">{i.price_value != null ? `Rs ${(i.price_value * i.qty).toLocaleString()}` : i.price}</span></div>)}
          {o.total != null && <div className="flex justify-between font-semibold border-t border-white/10 mt-2 pt-2"><span>Total (pay at pickup)</span><span>Rs {Number(o.total).toLocaleString()}</span></div>}
          {o.pickup_time && <p className="text-xs text-muted-foreground mt-2">Pickup: {o.pickup_time}</p>}
          {o.note && <p className="text-xs text-muted-foreground">Note: {o.note}</p>}
        </div>
        <p className="text-xs text-muted-foreground text-center mt-4">Keep this page open — it updates by itself. Bookmark it to come back.</p>
        <Link to={home} className="block text-center mt-4"><Button variant="outline" className="rounded-full border-white/15">Back to menu</Button></Link>
      </div>
    </div>
  );
}
